import React, {
    useContext, useEffect, useMemo 
} from 'react';
import {
    Box, Typography, 
    useTheme
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import CardContainer from '../../components/cardContainer';
import Card from '../../components/card';
import {
    DiagnosticFormContext 
} from '../../../../contexts/form';
import {
    TypographyVariants 
} from '../../../../figmaTheme';

type Props = {
    sectionKey: string,
    subtitle?: string
};

export const SectionIntro = ({
    sectionKey,
    subtitle
}: Props): JSX.Element => {
    const formContext = useContext(DiagnosticFormContext);
    const section = useMemo(() => {
        return formContext?.sections.find(s => s.key === sectionKey);
    }, [sectionKey, formContext?.sections]);

    const title = useMemo(() => {
        if (sectionKey === 'plan') {
            return `${formContext.answers?.your_name ? `${formContext.answers.your_name}'s` : 'Your'} Sunnyside Plan`;
        } else if (sectionKey === 'checkout') {
            return `${formContext.answers?.your_name}, let's do this!`;
        }
        return section?.headerText;
    }, [
        sectionKey,
        section?.headerText,
        formContext.answers.your_name]
    );

    const useStyles = makeStyles((theme) => ({
        sections: {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            marginTop: '50px',
            transform: 'translate(0, 16.2px)'
        },
        section: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '80px',
            height: '80px'
        },
        background: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            width: '100%',
            height: '100%',
            animation: '$explode 0.5s forwards linear 0.25s'
        },
        icon: {
            width: '50%',
            height: '50%',
            transform: 'none',
            animation: '$fadeOut 0.35s forwards 0.25s'
        },
        goalsIcon: {
            width: '67.5%',
            height: '67.5%',
        },
        title: {
            fontFamily: 'Poppins',
            fontSize: '24px',
            lineHeight: '32px',
            fontWeight: 700,
            textAlign: 'center',
            color: theme.palette.neutral.main,
            margin: 0,
            marginBottom: '8px',
            marginTop: '50px',
            transform: 'translate(0, 16.2px)',
            animation: '$fadeOut 0.35s forwards 0s'
        },
        subtitle: {
            fontFamily: 'Poppins',
            fontSize: '16px',
            lineHeight: '24px',
            fontWeight: 400,
            textAlign: 'center',
            color: theme.palette.neutral.main,
            margin: 0,
            transform: 'translate(0, 16.2px)',
            animation: '$fadeOut 0.35s forwards 0s'
        },
        cardTakeOver: {
            backgroundColor: theme.palette.white,
            height: '100%',
            width: '100%',
            maxWidth: '692px',
            position: 'absolute',
            top: 0,
            borderRadius: '24px 24px 0 0',
            transform: 'translate(-24px, 100vh)',
            animation: '$cardSlideUp 0.25s forwards 1s'
        },
        '@keyframes fadeOut': {
            '0%': {
                opacity: '1' 
            },
            '100%': {
                opacity: '0' 
            },
        },
        '@keyframes backgroundSlideUp': {
            '0%': {
                transform: 'translate(0, calc(100vh + 24px))' 
            },
            '100%': {
                transform: 'translate(0, -24px)' 
            },
        },
        '@keyframes cardSlideUp': {
            '0%': {
                transform: 'translate(-24px, calc(100vh - 64px))' 
            },
            '100%': {
                transform: 'translate(-24px, 0)' 
            },
        },
        '@keyframes explode': {
            '0%': {
                backgroundColor: section?.colors.main,
                transform: 'scale(1.5)' 
            },
            '100%': {
                backgroundColor: section?.colors.background,
                transform: 'scale(40)' 
            },
        },
        '@keyframes shrink': {
            '0%': {
                transform: 'scale(1)' 
            },
            '100%': {
                transform: 'scale(0.05)' 
            },
        }
    }));
    const classes = useStyles();
    const theme = useTheme();

    useEffect(() => {
        setTimeout(() => {
            if (sectionKey === 'aboutYou') {
                formContext.startAboutYouSection();
            } else {
                formContext.goNext();
            }
        }, 1250);
    }, []);

    return (
        <CardContainer
            sectionKey={sectionKey}
            backgroundColor={theme.palette.white}
            hideHeaderText
        >
            <Card overflowVisible>
                <Box className={classes.sections}>
                    <Box className={classes.section}>
                        <Box
                            className={classes.background}
                            style={{
                                backgroundColor: section?.colors.main 
                            }}
                        >
                            <Box
                                className={`${classes.icon} ${section?.key === 'goals' ? classes.goalsIcon : ''}`}
                                style={{
                                    fill: section?.colors.buttonArrow 
                                }}
                            >
                                {section?.icon}
                            </Box>
                        </Box>
                    </Box>
                </Box>
                <Typography
                    variant={TypographyVariants.H2}
                    className={classes.title}
                >
                    {title || section?.headerText}
                </Typography>
                {subtitle && (
                    <Typography
                        variant={TypographyVariants.H5}
                        className={classes.subtitle}
                        dangerouslySetInnerHTML={{
                            __html: subtitle 
                        }}
                    />
                )}
                <Box className={classes.cardTakeOver} />
            </Card>
        </CardContainer>
    );
};

export default SectionIntro;
